import React, { useCallback, useRef, useEffect, useMemo, useState } from "react";
import webui from "@tinqin/tinqin-web-ui";
import { IComponentProps, SirenEntity, SirenEntityBuilder } from "../../common/utils/commonTypes";
import MapUtils from "@tinqin/tinqin-utils/src/map";
import Popover from "@tinqin/tinqin-ui-components/src/popover/Popover";
import Scrollbar from "@tinqin/tinqin-react-custom-scrollbars";
import useDebounce from "../../common/hooks/useDebounce";
import EntityBuilder from "@tinqin/tinqin-web-ui/src/utils/EntityBuilder";
const { getEntityChildren } = webui.getActionByName("utils");

//Input with autosuggestions rendering enum list or components list from the config
export const AutoSuggestInput: React.FC<IComponentProps> = props => {
    if (!props.data) {
        return webui.getUtils().getEmptyContent();
    }
    return <AutoSuggestInputCore {...props} />;
};
const AutoSuggestInputCore: React.FC<IComponentProps> = props => {
    const data = useMemo(() => (props.data ? props.data.properties || {} : null), [props]);
    const [active, setActive] = useState(false);
    const [predictions, setPredictions] = useState<SirenEntity["properties"][]>([]);

    const autoCompleteRef: React.RefObject<HTMLInputElement> = useRef();
    const popoverRef: React.RefObject<HTMLDivElement> = useRef();
    const debouncedAutoSuggestion: React.MutableRefObject<Function> = useRef<Function>();
    const parentClassifier: React.MutableRefObject<string> = useRef();

    const minCharactersForSuggestion: number = props.config.minCharactersForSuggestion || 5;
    const debounceTime: number = props.config.debounceTime || 1000;
    const dispatch = webui.getDispatchMethod();

    const {
        extraClasses,
        placeholder,
        autosuggestionServerSide,
        suggestionsType,
        scrollable
    }: {
        extraClasses: string;
        placeholder: string;
        autosuggestionServerSide: boolean;
        suggestionsType: "enum" | "component";
        scrollable: boolean;
    } = webui.getUtils().extractComponentProps(props, {
        additionalProps: [
            "extraClasses",
            "placeholder",
            "autosuggestionServerSide",
            "suggestionsType",
            "scrollable"
        ],
        propsDefaults: {
            extraClasses: "",
            placeholder: "",
            autosuggestionServerSide: false,
            suggestionsType: "component",
            scrollable: true
        }
    });

    const updatePredictions = useCallback(() => {
        const webuiUtils = webui.getUtils();
        if (suggestionsType === "enum") {
            setPredictions(webuiUtils.buildEnumData(props));
        }
        const suggestionEntity = dispatch(getEntityChildren(props.computedBindTo))[0];
        const suggestionEntityBuilderObject: SirenEntityBuilder = EntityBuilder.of(
            suggestionEntity,
            { inplace: true }
        );
        parentClassifier.current = suggestionEntityBuilderObject.getId();
        setPredictions(suggestionEntityBuilderObject.getChildren());
    }, [dispatch, props, suggestionsType]);

    useEffect(() => {
        updatePredictions();
    }, [updatePredictions]);

    //Dispatch actions from data and BE updates autosuggestions
    const autoSuggestion = useCallback(
        async (newValue: string) => {
            //Check if it's a server side autosuggestion
            if (autosuggestionServerSide && props.data && props.data?.actions.length) {
                const dispatchActions = webui.getActionByName("siren.dispatchSirenActions");
                await dispatch(
                    dispatchActions(props.data.actions, props.widgetId, props.computedBindTo)
                );
                updatePredictions();
            } else {
                //It will be client side autosuggestion with filter
                const newPredictions = predictions.filter(item =>
                    item.properties.label.toLowerCase().includes(newValue.toLocaleLowerCase())
                );
                dispatch(
                    webui.getActionByName("utils.setEntityChildren")(
                        props.computedBindTo,
                        newPredictions
                    )
                );
            }
        },
        [
            props,
            dispatch,
            autosuggestionServerSide,
            predictions,
            updatePredictions
        ]
    );

    //we want the autosuggestion debounced
    useEffect(() => {
        if (!debouncedAutoSuggestion.current) {
            debouncedAutoSuggestion.current = (value: string) => {
                autoSuggestion(value);
            };
        }
        return () => {
            debouncedAutoSuggestion.current = undefined;
          };
    }, [debouncedAutoSuggestion, autoSuggestion]);

    const onBlur = useCallback((): void => {
        webui.getUtils().dispatchSirenActions(props, ["focusOut"]);
        setTimeout(() => {
            if (autoCompleteRef.current) {
                setActive(false);
            }
        }, 300);
    }, [props]);

    const onFocus = useCallback((): void => {
        webui.getUtils().dispatchSirenActions(props, ["focusIn"]);
    }, [props]);

    const onKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (e.key === "Enter") {
                setActive(false);
                webui.getUtils().dispatchSirenActions(props, ["pressEnter"]);
            }
        },
        [props]
    );

    const debouncedOnChange = useDebounce(async (newValue: string) => {
        webui.getUtils().dispatchSirenActions(props, ["change"]);
        if (newValue.trim().length >= minCharactersForSuggestion) {
            await debouncedAutoSuggestion.current(newValue);
            setActive(true);
        } else {
            setActive(false);
        }
    }, debounceTime);

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            const newValue = e.currentTarget.value;
            webui.getUtils().saveState(props, newValue);
            debouncedOnChange(newValue);
        },
        [debouncedOnChange, props]
    );

    const onClearValue = useCallback(() => {
        webui.getUtils().saveState(props, "");
        autoCompleteRef.current.focus();
        },
        [props]
    );

    const onSelectSuggestion = useCallback(
        (e: React.MouseEvent<HTMLLIElement>): void => {
            const selectedSuggestion = e.currentTarget.getAttribute("data-value");
            setActive(false);
            webui.getUtils().saveState(props, selectedSuggestion);
            webui.getUtils().dispatchSirenActions(props, "change");
        },
        [props]
    );

    if (!props.data) {
        return webui.getUtils().getEmptyContent();
    }
    const buildItems = (options: Map<any, any>) => {
        return MapUtils.map(
            options,
            (_key: number, item: { label: string; code: string }, index: number) => {
                return (
                    <li
                        key={`option-${index}`}
                        tabIndex={index}
                        data-value={item.label}
                        onClick={onSelectSuggestion}
                    >
                        <span data-value={item.label} className="tq-text-node">
                            {item.label}
                        </span>
                    </li>
                );
            }
        );
    };

    let suggestions: JSX.Element[] = [];
    if (active) {
        if (predictions && predictions.length > 0) {
            if (suggestionsType === "enum") {
                const options = new Map();
                predictions.forEach(function(prediction: SirenEntity["properties"], id: number) {
                    id++;
                    options.set(id.toString(), {
                        label: prediction.getLabel(),
                        code: id.toString()
                    });
                });
                suggestions = buildItems(options);
            } else {
                suggestions = webui
                    .getUtils()
                    .resolveChildren(props, { predictions, parentClassifier: parentClassifier.current, onSelectSuggestion });
            }
        }
    }
    return (
        <div className={`tq-form-element ${extraClasses}`} onBlur={onBlur}>
            {data.label && (
                <div className="tq-label-container">
                    <label className="tq-label">
                        <span className="tq-text-node">
                            <span className="tq-hint-container">
                                <span className="tq-text-node">{data.label}</span>
                            </span>
                        </span>
                    </label>
                </div>
            )}
            <div className="tq-input-container  tq-text-input tq-has-feedback">
                <input
                    type="text"
                    className="tq-input"
                    value={data.value || ""}
                    onFocus={onFocus}
                    onChange={onChange}
                    onKeyDown={onKeyPress}
                    ref={autoCompleteRef}
                    placeholder={placeholder}
                    data-tq-id={props.computedBindTo}
                    tabIndex={0}
                />
                {props.config.clearValueOption && (
                    <button 
                        className="tq-button un-ds-button-only-icon un-search-clear-btn"
                        type="button" 
                        name="button" 
                        aria-label="delete search"
                        onClick={onClearValue}>
                        <i className="tq-icon-close"></i>
                    </button>
                )}
                {active && (suggestions.length > 0 || !popoverRef.current) && (
                    <Popover
                        ref={popoverRef}
                        extraClasses={"un-search-results-dropdown"}
                        preferredPosition="bottom-right"
                        minWidthToAnchor={true}
                        anchor={autoCompleteRef}
                        active={active}
                    >
                        <ul className="tq-drop-down-options">
                            {scrollable ? (
                                <Scrollbar
                                    hideTracksWhenNotNeeded
                                    autoHeight
                                    autoHeightMin={0}
                                    autoHeightMax={200}
                                >
                                    {suggestions}
                                </Scrollbar>
                            ) : (
                                suggestions
                            )}
                        </ul>
                    </Popover>
                )}
            </div>
        </div>
    );
};

export default webui.connectComponent({
    actions: {
        dispatchSirenActions: "siren.dispatchSirenActions",
        saveState: "component.saveState"
    }
})(AutoSuggestInput);
